import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import { Button } from 'reactstrap';
import './ActionDefinitionForm.scss';
import { RadioGroup } from '@mui/material';
import { Feedback, Field, Form, Input } from '@availity/form';
import * as yup from 'yup';
import ReviewDocumentForm from './ReviewDocumentForm/ReviewDocumentForm';
import ExpandableRadioButton from '../../../components/ExpandableRadioButton/ExpandableRadioButton';
import './TimelineActionYupValidator.ts';
import { useDispatch } from 'react-redux';
import { GetDocumentTemplates } from '../../../slices/marketplace/timeline/thunks/get-document-templates.thunk';
import { TimelineParty } from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { TimelineEvent } from '../../../slices/marketplace/timeline/models/timeline-event.model';
import { DocumentDetails } from '../../../slices/marketplace/timeline/models/document-details.model';
import {
    BuyerActionTypeGroups,
    BuyerExpressionOfInterestOptionsList,
    BuyerLinkActionGroupOptions,
    MarketplaceEventActionTypeList,
    NdaTermSelectionOptionsList,
    SellerActionTypeGroups,
    SellerLinkActionGroupOptions,
    TimelineActionType,
} from '../../../slices/marketplace/timeline/models/timeline-action-type.model';
import {
    defaultTimelineAction,
    TimelineAction,
} from '../../../slices/marketplace/timeline/models/timeline-actions.model';
import { DocumentSection } from '../../../slices/marketplace/timeline/models/document-section.model';
import { SellerEngagementDocumentForm } from './SellerEngagementDocumentForm/SellerEngagementDocumentForm';
import { Label } from 'reactstrap';
import { useStateSelector } from '../../../store/selectors';
import Helpers from '../../../utils/helper';
import { DocumentDetailsStatus } from '../../../slices/marketplace/timeline/models/enums/document-details-status.enum';
import { EngagementDocumentSource } from '../../../slices/marketplace/timeline/models/enums/engagement-document-source';
import createNotification from '../../../utils/createNotification';
import {
    ActionDocumentType,
    DocumentExecutionTypeOptionsMapping,
} from '../../../slices/marketplace/timeline/models/enums/action-document-type.enum';
import DocumentsHistoryPopup from '../DocumetsHistoryPopup/DocumetsHistoryPopup';
import { MarketplaceDocumentSectionField } from './DocumentSectionField/MarketplaceDocumentSectionField';
import TeaserInfoModal from '../TeaserInfoModal/TeaserInfoModal';
import { TeaserPracticeData } from '../../../slices/marketplace/timeline/models/teaser-data.model';
import { NdaTermSelectionDocumentTemplatesState } from '../../../slices/marketplace/timeline/models/nda-term-selection';
import { MarketplaceDocumentSectionFieldValidator } from './DocumentSectionField/MarketplaceDocumentSectionFieldValidator';
import BuyerSelectionForm from './BuyerSelectionForm/BuyerSelectionForm';
import { DocumentDetailsContentType } from '../../../slices/marketplace/timeline/models/enums/document-details-content-type';
import { SellerDataAndDocumentRequestForm } from './SellerDataAndDocumentRequestForm/SellerDataAndDocumentRequestForm';
import { LinkedDataRequestProject } from '../../../slices/marketplace/timeline/models/linked-data-request-project.model';
import { useFeatureFlag } from '../../../custom-hooks/useFeatureFlag';
import { FeatureFlagsEnum } from '../../FeatureFlags/FeatureFlags.enum';
import { TimelineEventStatus } from '../../../slices/marketplace/timeline/models/enums/timeline-event-status.enum';
import { EngagementProcessStatus } from '../../../slices/marketplace/timeline/models/enums/engagement-process-status.enum';

interface ActionDefinitionFormProps {
    party: TimelineParty;
    onSubmit(
        action: TimelineAction,
        documentDetails?: DocumentDetails,
        dataRequestProject?: LinkedDataRequestProject,
        ndaTermSelection?: NdaTermSelectionDocumentTemplatesState
    ): void;
    disabled: boolean;
    currentEvent: TimelineEvent;
    linkedAction?: TimelineAction;
    timelineEvents: TimelineEvent[];
    getDocumentDetails(): DocumentDetails | null;
    getDataRequestProject(id?: string): LinkedDataRequestProject | null;
    downloadFile(documentId?: string): void;
    documentsSections: DocumentSection[];
    documentDetailsList: DocumentDetails[];
    dataRequestProjects: LinkedDataRequestProject[];
    timelineId: number;
    teaserData: TeaserPracticeData[];
    onTeaserSave: (newValues: TeaserPracticeData[]) => Promise<void>;
    onUpdateConfirm: () => void;
    isTeaserConfirmed: boolean;
    setIsFileUploading(isUploading: boolean): void;
    setDocumentSectionList: (documentSections: DocumentSection[]) => void;
    isTemplate: boolean;
    ndaTermSelectionState: NdaTermSelectionDocumentTemplatesState;
}

const ActionDefinitionForm = forwardRef(
    (props: ActionDefinitionFormProps, ref) => {
        const documentTemplates = useStateSelector(
            (state) => state.timelines.documentTemplatesTable.templates
        );
        const is25Implemented = useFeatureFlag(
            FeatureFlagsEnum.FF_MrktplcSellers_2_5
        );
        const timeline = useStateSelector(
            (state) => state.timelines.currentTimeline
        );

        const dispatch = useDispatch() as any;
        const [value, setValue] = useState<
            BuyerActionTypeGroups | SellerActionTypeGroups
        >(null);
        const [
            isDocumentsHistoryPopupVisible,
            setIsDocumentsHistoryPopupVisible,
        ] = useState(false);

        const [isTeaserInfoModalVisible, setIsTeaserInfoModalVisible] =
            useState(false);

        const getDefaultDocumentDetails = () => {
            const defaultDocDetails: DocumentDetails = {
                id: Helpers.generateGUID(),
                name: '',
                status: DocumentDetailsStatus.RequiresAction,
                counterParty: TimelineParty.Buyer,
                sectionId: '',
                type: -1 as ActionDocumentType,
                contentType: DocumentDetailsContentType.General,
                isUnregisteredFile: true,
                source: -1 as EngagementDocumentSource,
                templateId: 0,
                fileName: '',
            };

            return defaultDocDetails;
        };

        const [documentDetails, setDocumentDetails] = useState({
            ...(props.getDocumentDetails() ?? getDefaultDocumentDetails()),
        });

        useEffect(() => {
            const selectedGroup =
                MarketplaceEventActionTypeList.find(
                    (f) => f.id === props.linkedAction?.actionType
                )?.group ?? null;

            setValue(selectedGroup);
            const MAX_INT_32 = 2147483647;
            dispatch(
                GetDocumentTemplates({
                    filter: {
                        filters: [],
                        logic: '',
                    },
                    pageNumber: 1,
                    pageSize: MAX_INT_32,
                    sort: {
                        dir: 'asc',
                        field: 'name',
                    },
                })
            );
        }, []);

        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            const indexOfEventWithRefToThisAction = props.linkedAction
                ? props.timelineEvents.findIndex(
                      (e) =>
                          e.linkedAction?.actionReferenceId ===
                          props.linkedAction?.id
                  )
                : -1;
            if (indexOfEventWithRefToThisAction > -1) {
                event.preventDefault();
                event.stopPropagation();
                createNotification(
                    `You can't change the Engagement document type because it is used in the event with index ${
                        indexOfEventWithRefToThisAction + 1
                    }.`,
                    'error'
                );

                return;
            }

            setValue(Number(event.target.value));
            setDocumentDetails({ ...getDefaultDocumentDetails() });
        };

        const formRef = useRef<any>();

        useImperativeHandle(ref, () => ({
            handleSubmit() {
                formRef?.current?.handleSubmit();
            },
        }));

        const getBuyerActionGroupForm = (
            actionGroup: BuyerActionTypeGroups
        ) => {
            switch (actionGroup) {
                case BuyerActionTypeGroups.DocumentExecution:
                    return (
                        <div className="buyer-documnent-execution-form">
                            <Form
                                innerRef={formRef}
                                initialValues={{
                                    actionType:
                                        TimelineActionType.BuyerDocumentExecution,
                                    documentExecutionType: documentDetails.type,
                                    documentTemplate:
                                        documentDetails.templateId,
                                    documentName: documentDetails.name,
                                    documentSection: documentDetails.sectionId,
                                }}
                                validationSchema={yup.object().shape({
                                    documentExecutionType: yup
                                        .number()
                                        .min(
                                            0,
                                            'Document Execution Type is required'
                                        ),
                                    documentTemplate: yup
                                        .number()
                                        .min(1, 'Template is required'),
                                    documentSection:
                                        MarketplaceDocumentSectionFieldValidator.documentSectionValidationRules(
                                            props.documentsSections,
                                            props.party
                                        ),
                                    documentName: yup
                                        .string()
                                        .trim()
                                        .required('Document name is required'),
                                })}
                                onSubmit={async (values) => {
                                    let docDetails = documentDetails;

                                    docDetails = {
                                        ...docDetails,
                                        name: values.documentName.trim(),
                                        type: values.documentExecutionType,
                                        templateId: values.documentTemplate,
                                        counterParty: TimelineParty.VetValue,
                                        sectionId: values.documentSection,
                                        source: EngagementDocumentSource.Template,
                                    };

                                    props.onSubmit(
                                        {
                                            ...defaultTimelineAction,
                                            actionType: Number(
                                                TimelineActionType.BuyerDocumentExecution
                                            ),
                                            referenceItemId: docDetails.id,
                                        },
                                        docDetails
                                    );
                                }}>
                                <div className="document-execution">
                                    <Label for="documentExecutionType">
                                        Action Type
                                    </Label>
                                    <Input
                                        type="select"
                                        name="documentExecutionType"
                                        disabled={props.disabled}>
                                        <option value={-1}>Select</option>
                                        {DocumentExecutionTypeOptionsMapping.map(
                                            (option) => (
                                                <option
                                                    key={`type-${option.value}`}
                                                    value={option.value}>
                                                    {option.label}
                                                </option>
                                            )
                                        )}
                                    </Input>
                                    <Feedback name="documentExecutionType" />

                                    <Label for="documentTemplate">
                                        Document Template
                                    </Label>
                                    <Input
                                        type="select"
                                        name="documentTemplate"
                                        disabled={props.disabled}>
                                        <option value={0}>Select</option>
                                        {documentTemplates.map((option) => (
                                            <option
                                                key={`template-${option.id}`}
                                                value={option.id}>
                                                {option.name}
                                            </option>
                                        ))}
                                    </Input>
                                    <Feedback name="documentTemplate" />
                                    <MarketplaceDocumentSectionField
                                        documentsSections={
                                            props.documentsSections
                                        }
                                        disabled={props.disabled}
                                        setDocumentSectionList={
                                            props.setDocumentSectionList
                                        }
                                        documentDetailsList={
                                            props.documentDetailsList
                                        }
                                        type={
                                            'buyers'
                                        }></MarketplaceDocumentSectionField>
                                    <Field
                                        className="field"
                                        name="documentName"
                                        label="Document Name"
                                        disabled={props.disabled}
                                    />
                                </div>
                            </Form>
                        </div>
                    );
                case BuyerActionTypeGroups.ExpresstionOfInterest:
                    return (
                        <Form
                            innerRef={formRef}
                            initialValues={{
                                actionType:
                                    BuyerExpressionOfInterestOptionsList.some(
                                        (s) =>
                                            s.value ===
                                            props.linkedAction?.actionType
                                    )
                                        ? props.linkedAction?.actionType
                                        : TimelineActionType.BuyerExpressionOfInterest,
                            }}
                            validationSchema={yup.object().shape({
                                actionType: yup
                                    .number()
                                    .uniqueActionValidation(
                                        props.timelineEvents ?? [],
                                        props.currentEvent
                                    ),
                            })}
                            onSubmit={(values) => {
                                props.onSubmit({
                                    ...defaultTimelineAction,
                                    actionType: Number(values.actionType),
                                });
                            }}>
                            <Input
                                type="select"
                                name="actionType"
                                disabled={props.disabled}>
                                {BuyerExpressionOfInterestOptionsList.map(
                                    (option) => (
                                        <option
                                            key={option.value}
                                            value={option.value}>
                                            {option.label}
                                        </option>
                                    )
                                )}
                            </Input>
                            <Feedback name="actionType" />
                        </Form>
                    );
                case BuyerActionTypeGroups.ReviewDocument:
                    return (
                        <ReviewDocumentForm
                            ref={formRef}
                            onSubmit={props.onSubmit}
                            documentDetails={documentDetails}
                            disabled={props.disabled}
                            downloadFile={props.downloadFile}
                            currentEvent={props.currentEvent}
                            timelineEvents={props.timelineEvents}
                            documentDetailsList={props.documentDetailsList}
                            documentsSections={props.documentsSections}
                            setDocumentSectionList={
                                props.setDocumentSectionList
                            }
                        />
                    );
                case BuyerActionTypeGroups.UploadDocument:
                    return (
                        <Form
                            innerRef={formRef}
                            initialValues={{
                                actionType:
                                    TimelineActionType.BuyerUploadDocument,
                                documentSection: documentDetails.sectionId,
                            }}
                            validationSchema={yup.object().shape({
                                documentSection:
                                    MarketplaceDocumentSectionFieldValidator.documentSectionValidationRules(
                                        props.documentsSections,
                                        props.party
                                    ),
                            })}
                            onSubmit={(values) => {
                                props.onSubmit({
                                    ...defaultTimelineAction,
                                    actionType: values.actionType,
                                });

                                let docDetails = documentDetails;

                                docDetails = {
                                    ...docDetails,
                                    name: 'NA',
                                    type: ActionDocumentType.Downloadable,
                                    counterParty: TimelineParty.VetValue,
                                    sectionId: values.documentSection,
                                    source: EngagementDocumentSource.BuyerUpload,
                                };

                                props.onSubmit(
                                    {
                                        ...defaultTimelineAction,
                                        actionType: values.actionType,
                                        referenceItemId: docDetails.id,
                                    },
                                    docDetails
                                );
                            }}>
                            <MarketplaceDocumentSectionField
                                disabled={props.disabled}
                                documentDetailsList={props.documentDetailsList}
                                documentsSections={props.documentsSections}
                                setDocumentSectionList={
                                    props.setDocumentSectionList
                                }
                                type={'buyers'}
                            />
                        </Form>
                    );
                case BuyerActionTypeGroups.Custom:
                    return (
                        <RadioGroup style={{ marginLeft: '1rem' }}>
                            {[
                                TimelineActionType.BuyerScheduleMeeting,
                                TimelineActionType.BuyerLoiInformation,
                            ].map((actionType) => {
                                return (
                                    <ExpandableRadioButton
                                        disabled={props.disabled}
                                        value={actionType}
                                        label={
                                            MarketplaceEventActionTypeList.find(
                                                (f) => f.id === actionType
                                            )?.name
                                        }
                                        isExpanded={false}
                                        expandableContent={''}
                                    />
                                );
                            })}
                        </RadioGroup>
                    );
                default:
                    break;
            }
        };

        const getSellerActionGroupForm = (
            actionGroup: SellerActionTypeGroups
        ) => {
            switch (actionGroup) {
                case SellerActionTypeGroups.DataAndDocumentRequest:
                    return (
                        <SellerDataAndDocumentRequestForm
                            ref={formRef}
                            disabled={props.disabled}
                            currentEvent={props.currentEvent}
                            onSubmit={props.onSubmit}
                            dataRequestProjects={
                                props.dataRequestProjects
                            }></SellerDataAndDocumentRequestForm>
                    );
                case SellerActionTypeGroups.EngagementDocument:
                    return (
                        <SellerEngagementDocumentForm
                            ref={formRef}
                            onSubmit={props.onSubmit}
                            disabled={props.disabled}
                            downloadFile={props.downloadFile}
                            documentDetails={documentDetails}
                            documentsSections={props.documentsSections}
                            timelineEvents={props.timelineEvents}
                            linkedAction={props.linkedAction}
                            currentEvent={props.currentEvent}
                            setIsFileUploading={props.setIsFileUploading}
                            setIsDocumentsHistoryPopupVisible={
                                setIsDocumentsHistoryPopupVisible
                            }
                            setDocumentSectionList={
                                props.setDocumentSectionList
                            }
                            documentDetailsList={props.documentDetailsList}
                            dataRequestProjects={props.dataRequestProjects}
                            isTemplate={props.isTemplate}
                        />
                    );
                case SellerActionTypeGroups.BuyerSelection:
                    return (
                        <BuyerSelectionForm
                            ref={formRef}
                            onSubmit={props.onSubmit}
                            disabled={props.disabled}
                            linkedAction={props.linkedAction}
                            currentEvent={props.currentEvent}
                            timelineEvents={props.timelineEvents}
                            party={props.party}
                            documentsSections={props.documentsSections}
                            getDocumentDetails={props.getDocumentDetails}
                            setDocumentSectionList={
                                props.setDocumentSectionList
                            }
                            documentDetailsList={
                                props.documentDetailsList
                            }></BuyerSelectionForm>
                    );
                case SellerActionTypeGroups.NdaTermSelection:
                    return (
                        <div className="nda-term-selection-form">
                            <Form
                                innerRef={formRef}
                                initialValues={{
                                    actionType:
                                        NdaTermSelectionOptionsList.some(
                                            (s) =>
                                                s.value ===
                                                props.linkedAction?.actionType
                                        )
                                            ? props.linkedAction?.actionType
                                            : TimelineActionType.SellerCustomSelectDataNdaTerms,
                                    noNsTemplate:
                                        props.ndaTermSelectionState
                                            ?.noNsTemplateId ?? 0,
                                    dvmAndStaffNsTemplate:
                                        props.ndaTermSelectionState
                                            ?.dvmAndStaffNsTemplateId ?? 0,
                                    dvmNsTemplate:
                                        props.ndaTermSelectionState
                                            ?.dvmNsTemplateId ?? 0,
                                    staffNsTemplate:
                                        props.ndaTermSelectionState
                                            ?.staffNsTemplateId ?? 0,
                                    documentName: documentDetails.name,
                                    documentSection: documentDetails.sectionId,
                                }}
                                validationSchema={yup.object().shape({
                                    actionType: yup
                                        .number()
                                        .uniqueGroupValidation(
                                            props.timelineEvents ?? [],
                                            props.currentEvent
                                        )
                                        .uniqueActionValidation(
                                            props.timelineEvents ?? [],
                                            props.currentEvent
                                        ),
                                    noNsTemplate: yup
                                        .number()
                                        .min(1, 'Template is required'),
                                    dvmAndStaffNsTemplate: yup
                                        .number()
                                        .min(1, 'Template is required'),
                                    dvmNsTemplate: yup
                                        .number()
                                        .min(1, 'Template is required'),
                                    staffNsTemplate: yup
                                        .number()
                                        .min(1, 'Template is required'),
                                    documentName: yup
                                        .string()
                                        .trim()
                                        .required('Document name is required'),
                                    documentSection:
                                        MarketplaceDocumentSectionFieldValidator.documentSectionValidationRules(
                                            props.documentsSections,
                                            props.party
                                        ),
                                })}
                                onSubmit={async (values) => {
                                    let docDetails = documentDetails;

                                    docDetails = {
                                        ...docDetails,
                                        name: values.documentName.trim(),
                                        counterParty: TimelineParty.VetValue,
                                        templateId: null,
                                        sectionId: values.documentSection,
                                    };
                                    props.onSubmit(
                                        {
                                            ...defaultTimelineAction,
                                            actionType: Number(
                                                values.actionType
                                            ),
                                            referenceItemId: docDetails.id,
                                        },
                                        docDetails,
                                        undefined,
                                        {
                                            id: Helpers.generateGUID(),
                                            noNsTemplateId: values.noNsTemplate,
                                            dvmAndStaffNsTemplateId:
                                                values.dvmAndStaffNsTemplate,
                                            dvmNsTemplateId:
                                                values.dvmNsTemplate,
                                            staffNsTemplateId:
                                                values.staffNsTemplate,
                                        }
                                    );
                                }}>
                                <div className="nda-term-selection">
                                    <Label for="documentTemplate">
                                        NDA Type
                                    </Label>
                                    <Input
                                        type="select"
                                        name="actionType"
                                        disabled={props.disabled}>
                                        {NdaTermSelectionOptionsList.map(
                                            (option) => (
                                                <option
                                                    key={option.value}
                                                    value={option.value}>
                                                    {option.label}
                                                </option>
                                            )
                                        )}
                                    </Input>
                                    <Feedback name="actionType" />
                                    <MarketplaceDocumentSectionField
                                        documentsSections={
                                            props.documentsSections
                                        }
                                        disabled={props.disabled}
                                        setDocumentSectionList={
                                            props.setDocumentSectionList
                                        }
                                        documentDetailsList={
                                            props.documentDetailsList
                                        }
                                        type="sellers"></MarketplaceDocumentSectionField>
                                    <Label for="noNsTemplate">
                                        Document vNoNS Template
                                    </Label>
                                    <Input
                                        type="select"
                                        name="noNsTemplate"
                                        disabled={props.disabled}>
                                        <option value={0}>Select</option>
                                        {documentTemplates.map((option) => (
                                            <option
                                                key={`template-${option.id}`}
                                                value={option.id}>
                                                {option.name}
                                            </option>
                                        ))}
                                    </Input>
                                    <Feedback name="noNsTemplate" />

                                    <Label for="dvmAndStaffNsTemplate">
                                        Document vDVM&StaffNS Template
                                    </Label>
                                    <Input
                                        type="select"
                                        name="dvmAndStaffNsTemplate"
                                        disabled={props.disabled}>
                                        <option value={0}>Select</option>
                                        {documentTemplates.map((option) => (
                                            <option
                                                key={`template-${option.id}`}
                                                value={option.id}>
                                                {option.name}
                                            </option>
                                        ))}
                                    </Input>
                                    <Feedback name="dvmAndStaffNsTemplate" />

                                    <Label for="dvmNsTemplate">
                                        Document vDVMNS Template
                                    </Label>
                                    <Input
                                        type="select"
                                        name="dvmNsTemplate"
                                        disabled={props.disabled}>
                                        <option value={0}>Select</option>
                                        {documentTemplates.map((option) => (
                                            <option
                                                key={`template-${option.id}`}
                                                value={option.id}>
                                                {option.name}
                                            </option>
                                        ))}
                                    </Input>
                                    <Feedback name="dvmNsTemplate" />

                                    <Label for="staffNsTemplate">
                                        Document vStaffNS Template
                                    </Label>
                                    <Input
                                        type="select"
                                        name="staffNsTemplate"
                                        disabled={props.disabled}>
                                        <option value={0}>Select</option>
                                        {documentTemplates.map((option) => (
                                            <option
                                                key={`template-${option.id}`}
                                                value={option.id}>
                                                {option.name}
                                            </option>
                                        ))}
                                    </Input>
                                    <Feedback name="staffNsTemplate" />

                                    <Field
                                        className="field"
                                        name="documentName"
                                        label="Document Name"
                                        disabled={props.disabled}
                                    />
                                </div>
                            </Form>
                        </div>
                    );

                case SellerActionTypeGroups.TeaserApproval:
                    return (
                        <Form
                            innerRef={formRef}
                            initialValues={{
                                actionType:
                                    TimelineActionType.SellerTeaserApproval,
                            }}
                            onSubmit={async (values) => {
                                props.onSubmit({
                                    ...defaultTimelineAction,
                                    actionType: Number(values.actionType),
                                });
                            }}>
                            <Button
                                className="teaser-popup-btn"
                                onClick={() =>
                                    setIsTeaserInfoModalVisible(true)
                                }
                                disabled={props.isTemplate}
                                type="button"
                                color="primary">
                                {`${
                                    props.isTeaserConfirmed
                                        ? 'View'
                                        : 'Configure'
                                } Teaser Information`}
                            </Button>
                        </Form>
                    );
                case SellerActionTypeGroups.Custom:
                    return (
                        <RadioGroup style={{ marginLeft: '1rem' }}>
                            {[
                                TimelineActionType.SellerCustomProcessDuration,
                                TimelineActionType.SellerCustomMeetingAvailability,
                            ].map((actionType) => {
                                return (
                                    <ExpandableRadioButton
                                        disabled={props.disabled}
                                        value={actionType}
                                        label={
                                            MarketplaceEventActionTypeList.find(
                                                (f) => f.id === actionType
                                            )?.name
                                        }
                                        isExpanded={false}
                                        expandableContent={''}
                                    />
                                );
                            })}
                        </RadioGroup>
                    );
                default:
                    break;
            }
        };

        const buyerActionsForm = () => (
            <RadioGroup value={value} onChange={handleChange}>
                {BuyerLinkActionGroupOptions.map((actionGroup) => {
                    //remove isImplemented flag when all actions are available
                    switch (actionGroup.id) {
                        case BuyerActionTypeGroups.ExpresstionOfInterest:
                            return actionGroup.isImplemented ? (
                                <ExpandableRadioButton
                                    disabled={props.disabled}
                                    value={actionGroup.id}
                                    label={actionGroup.name}
                                    isExpanded={value === actionGroup.id}
                                    expandableContent={getBuyerActionGroupForm(
                                        actionGroup.id
                                    )}
                                />
                            ) : (
                                <></>
                            );

                        default:
                            return actionGroup.isImplemented ? (
                                <ExpandableRadioButton
                                    disabled={props.disabled}
                                    value={actionGroup.id}
                                    label={actionGroup.name}
                                    isExpanded={value === actionGroup.id}
                                    expandableContent={getBuyerActionGroupForm(
                                        actionGroup.id
                                    )}
                                />
                            ) : (
                                <></>
                            );
                    }
                })}
            </RadioGroup>
        );

        const sellerActionsForm = () => (
            <RadioGroup value={value} onChange={handleChange}>
                {SellerLinkActionGroupOptions.filter(
                    (o) =>
                        o.id !==
                            SellerActionTypeGroups.DataAndDocumentRequest ||
                        is25Implemented
                ).map((actionGroup) => {
                    return actionGroup.isImplemented ? (
                        <ExpandableRadioButton
                            disabled={props.disabled}
                            value={actionGroup.id}
                            label={actionGroup.name}
                            isExpanded={value === actionGroup.id}
                            expandableContent={getSellerActionGroupForm(
                                actionGroup.id
                            )}
                        />
                    ) : (
                        <></>
                    );
                })}
            </RadioGroup>
        );

        const renderForm = () => {
            switch (props.party) {
                case TimelineParty.Buyer:
                    return buyerActionsForm();
                case TimelineParty.Seller:
                    return sellerActionsForm();
                default:
                    return 'N/A';
            }
        };

        const filterDocumentDetails = (documentDetails: DocumentDetails[]) => {
            if (!props.linkedAction) return documentDetails;
            let docDetailsList = props.documentDetailsList.filter(
                (d) =>
                    d.source === EngagementDocumentSource.AdminUpload &&
                    (props.linkedAction.referenceItemId == d.id ||
                        props.linkedAction.oldReferenceItemIds?.includes(d.id))
            );
            docDetailsList.sort((a, b) => {
                return new Date(a.datePosted) > new Date(b.datePosted) ? -1 : 1;
            });
            return docDetailsList;
        };

        return (
            <>
                {isDocumentsHistoryPopupVisible && (
                    <DocumentsHistoryPopup
                        downloadDocument={props.downloadFile}
                        oldDocumentDetailsList={filterDocumentDetails(
                            props.documentDetailsList
                        )}
                        isVisible={isDocumentsHistoryPopupVisible}
                        onClose={() => setIsDocumentsHistoryPopupVisible(false)}
                    />
                )}

                {isTeaserInfoModalVisible && (
                    <TeaserInfoModal
                        timelineId={props.timelineId}
                        teaserData={props.teaserData}
                        isAdmin={true}
                        isVisible={isTeaserInfoModalVisible}
                        onClose={() => setIsTeaserInfoModalVisible(false)}
                        onSaveToTempState={props.onTeaserSave}
                        onUpdateConfirm={props.onUpdateConfirm}
                        isReadOnly={
                            props.isTeaserConfirmed ||
                            props.currentEvent?.status ===
                                TimelineEventStatus.Completed ||
                            timeline.engagementProcess?.status ===
                                EngagementProcessStatus.Completed
                        }
                    />
                )}

                <div className="action-definition-form">{renderForm()}</div>
            </>
        );
    }
);

export default ActionDefinitionForm;
